import VpnKeyOffIcon from "@mui/icons-material/VpnKeyOff";
import { useQueryClient } from "@tanstack/react-query";

import { PermissionsEnum } from "@/auth/interfaces";
import { useColumnsUsuarioSistema } from "@/erp/administration/usuarios/shared/hooks";
import { TABLE_CONSTANTS_COLUMS } from "@/shared";
import { useUnblockUser } from "@/shared/actions/erp";
import {
  UsuarioSistemaQueryKeys,
  useFetchUsuariosSistema,
} from "@/shared/actions/erp/administration/usuarios";
import {
  CustomSearch,
  GeneralTable,
  SingleTableBoxScene,
} from "@/shared/components/ui";
import {
  useCheckPermissions,
  useTableServerSideFiltering,
} from "@/shared/hooks";
import { useTableFilter } from "@/shared/hooks/useTableFilter";
import { UsuariosSistemaGET } from "@/shared/interfaces";
import { hasAllPermissions } from "@/shared/utils";
import { useUiConfirmModalStore, useUiStore } from "@/store/ui";

export type BlockedUsersPassWrongPageProps = {};

const BlockedUsersPassWrongPage: React.FC<
  BlockedUsersPassWrongPageProps
> = () => {
  useCheckPermissions(PermissionsEnum.auth_view_user);

  ///* hooks
  const queryClient = useQueryClient();

  // server side filters - colums table
  const { filterObject, columnFilters, setColumnFilters } =
    useTableServerSideFiltering();

  ///* global state
  const setConfirmDialog = useUiConfirmModalStore((s) => s.setConfirmDialog);
  const setConfirmDialogIsOpen = useUiConfirmModalStore(
    (s) => s.setConfirmDialogIsOpen
  );
  const setIsGlobalLoading = useUiStore((s) => s.setIsGlobalLoading);

  ///* table
  const {
    globalFilter,
    pagination,
    searchTerm,
    onChangeFilter,
    setPagination,
  } = useTableFilter();
  const { pageIndex, pageSize } = pagination;

  ///* fetch data
  const {
    data: usuariosSistemaPagingRes,
    isLoading,
    isRefetching,
  } = useFetchUsuariosSistema({
    enabled: true,
    params: {
      page: pageIndex + 1,
      page_size: pageSize,
      razon_social: searchTerm,
      ...filterObject,
      is_active: false,
    },
  });

  ///* mutations
  const unBlockUser = useUnblockUser({
    enableRedirect: false,
  });

  ///* handlers
  const onEdit = (user: UsuariosSistemaGET) => {
    setConfirmDialog({
      isOpen: true,
      title: "Desbloquear Usuario",
      subtitle: "¿Está seguro que desea desbloquear este usuario?",
      confirmTextBtn: "SI",
      btnTitle: "NO",
      onConfirm: async () => {
        setIsGlobalLoading(true);
        setConfirmDialogIsOpen(false);

        await unBlockUser.mutateAsync({
          username: user?.username,
        });

        queryClient.invalidateQueries({
          queryKey: [UsuarioSistemaQueryKeys.USUARIOS_SISTEMA],
        });
        setIsGlobalLoading(false);
      },
    });
  };

  ///* columns
  const { columnsSystemUser } = useColumnsUsuarioSistema();

  return (
    <SingleTableBoxScene
      title="Usuarios Bloqueados por Credenciales Incorrectas"
      enableRedirect={false}
    >
      <CustomSearch
        onChange={onChangeFilter}
        value={globalFilter}
        text="por nombre"
      />

      <GeneralTable<UsuariosSistemaGET>
        columns={columnsSystemUser}
        data={usuariosSistemaPagingRes?.data?.data || []}
        isLoading={isLoading}
        isRefetching={isRefetching}
        // // filters - server side
        enableManualFiltering={true}
        columnFilters={columnFilters}
        onColumnFiltersChange={setColumnFilters}
        // // search
        enableGlobalFilter={false}
        // // pagination
        pagination={pagination}
        onPaging={setPagination}
        rowCount={usuariosSistemaPagingRes?.data?.count}
        // // actions
        actionsColumnSize={TABLE_CONSTANTS_COLUMS.ACTIONCOLUMN_WIDTH}
        enableEditing={hasAllPermissions([PermissionsEnum.auth_change_user])}
        enableColumnActions={hasAllPermissions([
          PermissionsEnum.auth_change_user,
        ])}
        // crud
        canEdit={hasAllPermissions([PermissionsEnum.auth_change_user])}
        onEdit={onEdit}
        editIcon={<VpnKeyOffIcon />}
        toolTipTitleEditIcon="Desbloquear Usuario"
        editIconColor="success"
        canDelete={false}
      />
    </SingleTableBoxScene>
  );
};

export default BlockedUsersPassWrongPage;
